import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TenantAdminLayout from '../Layout/TenantAdminLayout';
import RazorpayCheckout from '../../components/Payment/RazorpayCheckout';
import AlertSettings from './AlertSettings';
import {
  FiMail,
  FiMessageSquare,
  FiRefreshCw,
  FiPlus,
  FiDownload,
  FiSearch,
  FiX,
  FiCheck,
  FiAlertCircle,
  FiCreditCard,
  FiSave,
  FiHome,
  FiSettings,
  FiList,
  FiDollarSign,
  FiGift,
  FiChevronRight,
  FiPercent,
  FiBell,
  FiTrendingUp,
  FiInfo,
  FiClock,
  FiEye,
  FiUser,
  FiSliders,
} from 'react-icons/fi';
import api from '../../api';

const CommunicationPanel = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const tenantId = location.state?.tenantId || localStorage.getItem('tenantId');
  
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [savingUseCases, setSavingUseCases] = useState(false);
  
  const [settings, setSettings] = useState({
    communicationEnabled: false,
    emailEnabled: false,
    smsEnabled: false,
    emailUseCases: [],
    smsUseCases: [],
    emailUseCaseLabels: {},
    smsUseCaseLabels: {}
  });
  
  const [localEmailUseCases, setLocalEmailUseCases] = useState([]);
  const [localSmsUseCases, setLocalSmsUseCases] = useState([]);
  const [allEmailUseCaseLabels, setAllEmailUseCaseLabels] = useState({});
  const [allSmsUseCaseLabels, setAllSmsUseCaseLabels] = useState({});
  
  const [wallet, setWallet] = useState({
    emailCredits: 0,
    smsCredits: 0,
    emailTotalUsed: 0,
    smsTotalUsed: 0,
    totalEmailsSent: 0,
    totalSmsSent: 0
  });
  
  const [pricing, setPricing] = useState({
    email: { 
      basePrice: 0, 
      price: 0, 
      discount: 0, 
      discountType: 'none',
      validTo: null,
      fixedPrice: null,
      currencySymbol: '₹',
      currencyCode: 'INR'
    },
    sms: { 
      basePrice: 0, 
      price: 0, 
      discount: 0, 
      discountType: 'none',
      validTo: null,
      fixedPrice: null,
      currencySymbol: '₹',
      currencyCode: 'INR'
    },
    tax: {
      name: 'Tax',
      percentage: 0,
      isInclusive: true,
      source: 'global',
      description: 'Tax'
    },
    hasDiscount: false
  });
  
  const [tenantPricing, setTenantPricing] = useState({
    overrides: [],
    bulkPricing: [],
    summary: {
      hasActiveDiscount: false,
      hasActiveBulkPricing: false,
      activeDiscountCount: 0,
      activeBulkPricingCount: 0
    }
  });
  
  const [bundles, setBundles] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [logs, setLogs] = useState([]);
  
  // ============================================
  // ANALYTICS STATE
  // ============================================
  const [detailedUsage, setDetailedUsage] = useState(null);
  const [, setUsageTrend] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // ============================================
  // ALERTS STATE
  // ============================================
  const [alerts, setAlerts] = useState([]);
  const [alertFilters, setAlertFilters] = useState({
    type: 'all',
    status: 'all',
    search: '',
    dateRange: 'week'
  });
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertStats, setAlertStats] = useState({
    total: 0,
    unread: 0,
    resolved: 0,
    pending: 0
  });
  
  // Modals
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedBundleDetails, setSelectedBundleDetails] = useState(null);
  
  // Alerts
  const [alertConfig, setAlertConfig] = useState({ show: false, title: '', message: '', type: 'info' });
  const [confirmConfig, setConfirmConfig] = useState({ show: false, title: '', message: '', onConfirm: () => {} });

  // Recharge Form
  const [rechargeForm, setRechargeForm] = useState({
    type: 'email',
    credits: 100,
    paymentMethod: 'card',
    useBulkPricing: false,
    selectedBulkTier: null
  });
  
  // Filters
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    search: '',
    dateRange: 'month'
  });

  const triggerAlert = (title, message, type = 'info') => {
    setAlertConfig({ show: true, title, message, type });
  };

  // ============================================
  // HELPER FUNCTIONS FOR BULK PRICING
  // ============================================
  
  const getActiveBulkPricing = (type) => {
    const active = tenantPricing.bulkPricing.filter(
      b => b.communicationType === type && b.isCurrentlyActive
    ).sort((a, b) => a.minCredits - b.minCredits);
    return active;
  };

  const getBestBulkPrice = (type, credits) => {
    const activeBulks = getActiveBulkPricing(type);
    let bestBulk = null;
    
    for (const bulk of activeBulks) {
      if (credits >= bulk.minCredits) {
        if (!bestBulk || bulk.specialPrice < bestBulk.specialPrice) {
          bestBulk = bulk;
        }
      }
    }
    
    return bestBulk;
  };

  const getEffectivePriceWithBulk = (type, credits) => {
    const basePrice = type === 'email' ? pricing.email.price : pricing.sms.price;
    const bestBulk = getBestBulkPrice(type, credits);
    
    if (bestBulk) {
      return {
        price: bestBulk.specialPrice,
        isBulk: true,
        bulkTier: bestBulk,
        savings: (basePrice - bestBulk.specialPrice) * credits
      };
    }
    
    return {
      price: basePrice,
      isBulk: false,
      bulkTier: null,
      savings: 0
    };
  };

  // ============================================
  // FETCH ANALYTICS DATA
  // ============================================
  const fetchDetailedUsage = useCallback(async () => {
    if (!tenantId) return;
    
    try {
      setAnalyticsLoading(true);
      const response = await api.get('/api/tenant/communication/usage/detailed', {
        params: {
          startDate: filters.dateRange === 'today' ? new Date().toISOString().split('T')[0] : undefined,
          communicationType: filters.type === 'all' ? undefined : filters.type,
          useCase: filters.useCase || undefined,
          status: filters.status === 'all' ? undefined : filters.status
        }
      });
      
      if (response.data.success) {
        setDetailedUsage(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch detailed usage:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [tenantId, filters]);

  const fetchUsageTrend = useCallback(async () => {
    if (!tenantId) return;
    
    try {
      const response = await api.get('/api/tenant/communication/usage/trend', {
        params: {
          period: filters.dateRange,
          communicationType: filters.type === 'all' ? undefined : filters.type
        }
      });
      
      if (response.data.success) {
        setUsageTrend(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch usage trend:', error);
    }
  }, [tenantId, filters.dateRange, filters.type]);

  // ============================================
  // LOAD DATA FROM API
  // ============================================
  const fetchAllData = useCallback(async () => {
    if (!tenantId) {
      triggerAlert('Login Error', 'Please sign in again.', 'error');
      navigate('/signin');
      return;
    }
    
    try {
      setLoading(true);
      setRefreshing(true);
      
      const [
        settingsRes, walletRes, pricingRes, 
        bundlesRes, transactionsRes, logsRes, tenantPricingRes,
        alertsRes
      ] = await Promise.all([
        api.get('/api/tenant/communication/settings'),
        api.get('/api/tenant/wallet/balance'),
        api.get('/api/tenant/wallet/pricing'),
        api.get('/api/tenant/bundles'),
        api.get(`/api/tenant/wallet/transactions?page=1&limit=10`),
        api.get(`/api/tenant/communication/logs?page=1&limit=10`),
        api.get('/api/tenant/pricing').catch((err) => {
          console.error('❌ Failed to fetch tenant pricing:', err);
          return { data: { success: false } };
        }),
        api.get('/api/tenant/alerts').catch((err) => {
          console.error('❌ Failed to fetch alerts:', err);
          return { data: { success: false, data: [] } };
        })
      ]);
      
      if (settingsRes.data.success) {
        const settingsData = settingsRes.data.data;
        setSettings(settingsData);
        setLocalEmailUseCases(settingsData.emailUseCases || []);
        setLocalSmsUseCases(settingsData.smsUseCases || []);
        
        if (settingsData.emailUseCaseLabels && Object.keys(settingsData.emailUseCaseLabels).length > 0) {
          setAllEmailUseCaseLabels(settingsData.emailUseCaseLabels);
        }
        if (settingsData.smsUseCaseLabels && Object.keys(settingsData.smsUseCaseLabels).length > 0) {
          setAllSmsUseCaseLabels(settingsData.smsUseCaseLabels);
        }
      }
      if (walletRes.data.success) setWallet(walletRes.data.data);
      
      if (pricingRes.data.success) {
        const pricingData = pricingRes.data.data;
        setPricing({
          email: {
            basePrice: pricingData.email?.basePrice || 0,
            price: pricingData.email?.price || 0,
            discount: pricingData.email?.discount || 0,
            discountType: pricingData.email?.discountType || 'none',
            validTo: pricingData.email?.validTo || null,
            fixedPrice: pricingData.email?.fixedPrice || null,
            currencySymbol: pricingData.email?.currencySymbol || '₹',
            currencyCode: pricingData.email?.currencyCode || 'INR'
          },
          sms: {
            basePrice: pricingData.sms?.basePrice || 0,
            price: pricingData.sms?.price || 0,
            discount: pricingData.sms?.discount || 0,
            discountType: pricingData.sms?.discountType || 'none',
            validTo: pricingData.sms?.validTo || null,
            fixedPrice: pricingData.sms?.fixedPrice || null,
            currencySymbol: pricingData.sms?.currencySymbol || '₹',
            currencyCode: pricingData.sms?.currencyCode || 'INR'
          },
          tax: {
            name: pricingData.tax?.name || 'Tax',
            percentage: pricingData.tax?.percentage || 0,
            isInclusive: pricingData.tax?.isInclusive !== undefined ? pricingData.tax.isInclusive : true,
            source: pricingData.tax?.source || 'global',
            description: pricingData.tax?.description || 'Tax'
          },
          hasDiscount: pricingData.hasDiscount || false
        });
      }
      
      if (tenantPricingRes?.data?.success) {
        const tpData = tenantPricingRes.data.data;
        setTenantPricing({
          overrides: tpData.overrides || [],
          bulkPricing: tpData.bulkPricing || [],
          summary: tpData.summary || {
            hasActiveDiscount: false,
            hasActiveBulkPricing: false,
            activeDiscountCount: 0,
            activeBulkPricingCount: 0
          }
        });
      }
      
      if (bundlesRes.data.success) setBundles(bundlesRes.data.data);
      if (transactionsRes.data.success) setTransactions(transactionsRes.data.data);
      if (logsRes.data.success) setLogs(logsRes.data.data);
      
      // Set alerts data
      if (alertsRes.data.success) {
        const alertData = alertsRes.data.data || [];
        setAlerts(alertData);
        
        // Calculate stats
        const total = alertData.length;
        const unread = alertData.filter(a => a.status === 'unread' || a.read === false).length;
        const resolved = alertData.filter(a => a.status === 'resolved' || a.resolved === true).length;
        const pending = alertData.filter(a => a.status === 'pending' || a.status === 'sent').length;
        
        setAlertStats({ total, unread, resolved, pending });
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) navigate('/signin');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tenantId, navigate]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData, filters.dateRange]);

  // Fetch analytics when switching to analytics tab
  useEffect(() => {
    if (activeTab === 'analytics' && tenantId) {
      fetchDetailedUsage();
      fetchUsageTrend();
    }
  }, [activeTab, tenantId, fetchDetailedUsage, fetchUsageTrend]);

  // ============================================
  // ACTIONS
  // ============================================
  const handleToggleCommunication = async (type) => {
    try {
      const updatedSettings = { ...settings };
      if (type === 'master') updatedSettings.communicationEnabled = !settings.communicationEnabled;
      else if (type === 'email') updatedSettings.emailEnabled = !settings.emailEnabled;
      else if (type === 'sms') updatedSettings.smsEnabled = !settings.smsEnabled;
      
      const response = await api.put('/api/tenant/communication/settings', updatedSettings);
      if (response.data.success) {
        setSettings(response.data.data);
        if (response.data.data.emailUseCases) setLocalEmailUseCases(response.data.data.emailUseCases);
        if (response.data.data.smsUseCases) setLocalSmsUseCases(response.data.data.smsUseCases);
        triggerAlert('Success', 'Settings updated.', 'success');
      }
    } catch {
      triggerAlert('Error', 'Failed to save changes.', 'error');
    }
  };

  const handleLocalEmailUseCaseChange = (key) => {
    setLocalEmailUseCases(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleLocalSmsUseCaseChange = (key) => {
    setLocalSmsUseCases(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleSaveUseCases = async () => {
    setSavingUseCases(true);
    try {
      const updatedSettings = { 
        ...settings,
        emailUseCases: localEmailUseCases,
        smsUseCases: localSmsUseCases,
        emailUseCaseLabels: allEmailUseCaseLabels,
        smsUseCaseLabels: allSmsUseCaseLabels
      };
      
      const response = await api.put('/api/tenant/communication/settings', updatedSettings);
      
      if (response.data.success) {
        setSettings(response.data.data);
        setLocalEmailUseCases(response.data.data.emailUseCases || []);
        setLocalSmsUseCases(response.data.data.smsUseCases || []);
        triggerAlert('Saved', 'Settings updated successfully.', 'success');
      }
    } catch (error) {
      triggerAlert('Error', 'Could not save changes.', 'error');
    } finally {
      setSavingUseCases(false);
    }
  };

  const handleResetUseCases = () => {
    setLocalEmailUseCases(settings.emailUseCases || []);
    setLocalSmsUseCases(settings.smsUseCases || []);
    triggerAlert('Notice', 'Changes discarded.', 'info');
  };

  const handleExportLogs = async () => {
    try {
      const response = await api.post('/api/tenant/communication/logs/export', {
        type: filters.type === 'all' ? undefined : filters.type,
        status: filters.status === 'all' ? undefined : filters.status,
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `logs-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      triggerAlert('Download Failed', 'Could not export logs.', 'error');
    }
  };

  // ============================================
  // ALERT ACTIONS
  // ============================================
  const handleMarkAlertRead = async (alertId) => {
    try {
      const response = await api.put(`/api/tenant/alerts/${alertId}/read`);
      if (response.data.success) {
        setAlerts(prev => prev.map(a => 
          a.id === alertId ? { ...a, read: true, status: 'read' } : a
        ));
        setAlertStats(prev => ({
          ...prev,
          unread: prev.unread - 1,
          read: (prev.read || 0) + 1
        }));
        triggerAlert('Success', 'Alert marked as read.', 'success');
      }
    } catch (error) {
      console.error('Failed to mark alert as read:', error);
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      const response = await api.put(`/api/tenant/alerts/${alertId}/resolve`);
      if (response.data.success) {
        setAlerts(prev => prev.map(a => 
          a.id === alertId ? { ...a, status: 'resolved', resolved: true } : a
        ));
        setAlertStats(prev => ({
          ...prev,
          resolved: prev.resolved + 1,
          pending: prev.pending - 1
        }));
        triggerAlert('Success', 'Alert resolved successfully.', 'success');
      }
    } catch (error) {
      console.error('Failed to resolve alert:', error);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    setConfirmConfig({
      show: true,
      title: 'Delete Alert',
      message: 'Are you sure you want to delete this alert? This action cannot be undone.',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/api/tenant/alerts/${alertId}`);
          if (response.data.success) {
            setAlerts(prev => prev.filter(a => a.id !== alertId));
            const total = alerts.length - 1;
            const unread = alerts.filter(a => a.id !== alertId && (a.status === 'unread' || a.read === false)).length;
            const resolved = alerts.filter(a => a.id !== alertId && (a.status === 'resolved' || a.resolved === true)).length;
            const pending = alerts.filter(a => a.id !== alertId && (a.status === 'pending' || a.status === 'sent')).length;
            setAlertStats({ total, unread, resolved, pending });
            triggerAlert('Success', 'Alert deleted successfully.', 'success');
          }
        } catch (error) {
          console.error('Failed to delete alert:', error);
          triggerAlert('Error', 'Failed to delete alert.', 'error');
        }
        setConfirmConfig(prev => ({ ...prev, show: false }));
      }
    });
  };

  // ============================================
  // HELPERS
  // ============================================
  const formatPrice = (price) => {
    const symbol = pricing.email.currencySymbol || '₹';
    return `${symbol}${Number(price || 0).toFixed(2)}`;
  };
  
  const formatNumber = (num) => num?.toLocaleString('en-IN') || '0';
  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never';

  const getStatusStyle = (status) => {
    const maps = {
      sent: 'bg-emerald-100 text-emerald-800',
      delivered: 'bg-blue-100 text-blue-800',
      failed: 'bg-rose-100 text-rose-800',
      pending: 'bg-amber-100 text-amber-800',
      unread: 'bg-indigo-100 text-indigo-800',
      read: 'bg-slate-100 text-slate-800',
      resolved: 'bg-emerald-100 text-emerald-800',
    };
    return maps[status] || 'bg-slate-100 text-slate-800';
  };

  const getAlertIcon = (type) => {
    const maps = {
      email: <FiMail className="w-4 h-4" />,
      sms: <FiMessageSquare className="w-4 h-4" />,
      system: <FiBell className="w-4 h-4" />,
      attendance: <FiUser className="w-4 h-4" />,
      fee: <FiDollarSign className="w-4 h-4" />,
    };
    return maps[type] || <FiBell className="w-4 h-4" />;
  };

  const getAlertColor = (type) => {
    const maps = {
      email: 'blue',
      sms: 'purple',
      system: 'indigo',
      attendance: 'green',
      fee: 'amber',
    };
    return maps[type] || 'indigo';
  };

  const hasUnsavedChanges = () => {
    const emailChanged = JSON.stringify(localEmailUseCases.sort()) !== JSON.stringify((settings.emailUseCases || []).sort());
    const smsChanged = JSON.stringify(localSmsUseCases.sort()) !== JSON.stringify((settings.smsUseCases || []).sort());
    return emailChanged || smsChanged;
  };

  const tabs = [
    { id: 'dashboard', label: 'Overview', icon: <FiHome className="w-4 h-4" /> },
    { id: 'settings', label: 'Turn on/off Email/SMS', icon: <FiSettings className="w-4 h-4" /> },
    { id: 'alert-config', label: 'Absent Alert Settings', icon: <FiSliders className="w-4 h-4" /> },
    { id: 'analytics', label: 'Credits Usage Log', icon: <FiTrendingUp className="w-4 h-4" /> },
    { id: 'logs', label: 'Failure Logs', icon: <FiList className="w-4 h-4" /> },
    { id: 'wallet', label: 'Credits Purchase History', icon: <FiDollarSign className="w-4 h-4" /> },
    { id: 'bundles', label: 'Packages', icon: <FiGift className="w-4 h-4" /> }
  ];

  // Loading state with skeleton
  if (loading) {
    return (
      <TenantAdminLayout>
        <div className="min-h-screen bg-slate-50 pb-20">
          {/* Header Skeleton */}
          <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
            <div className="max-w-7xl mx-auto px-3 sm:px-4">
              <div className="flex items-center justify-between h-14 sm:h-16">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-slate-200 rounded-lg animate-pulse" />
                  <div>
                    <div className="w-32 h-4 bg-slate-200 rounded animate-pulse" />
                    <div className="w-48 h-3 bg-slate-100 rounded animate-pulse mt-1" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-8 bg-slate-200 rounded-lg animate-pulse" />
                  <div className="w-8 h-8 bg-slate-200 rounded-lg animate-pulse" />
                </div>
              </div>
            </div>
          </div>
          
          <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 space-y-3 sm:space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
                <div className="w-20 h-4 bg-slate-200 rounded animate-pulse mb-2" />
                <div className="w-24 h-6 bg-slate-200 rounded animate-pulse" />
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
                <div className="w-20 h-4 bg-slate-200 rounded animate-pulse mb-2" />
                <div className="w-24 h-6 bg-slate-200 rounded animate-pulse" />
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="space-y-3">
                <div className="w-32 h-4 bg-slate-200 rounded animate-pulse" />
                <div className="grid grid-cols-3 gap-3">
                  <div className="h-12 bg-slate-100 rounded-lg animate-pulse" />
                  <div className="h-12 bg-slate-100 rounded-lg animate-pulse" />
                  <div className="h-12 bg-slate-100 rounded-lg animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </TenantAdminLayout>
    );
  }

  // Get active bulk pricing for current type
  const activeBulkPricing = getActiveBulkPricing(rechargeForm.type);
  const hasBulkPricing = activeBulkPricing.length > 0;
  
  // Calculate current price
  const currentBasePrice = rechargeForm.type === 'email' ? pricing.email.price : pricing.sms.price;
  const effectivePriceInfo = getEffectivePriceWithBulk(rechargeForm.type, rechargeForm.credits);
  const pricePerCredit = rechargeForm.useBulkPricing && effectivePriceInfo.isBulk 
    ? effectivePriceInfo.price 
    : currentBasePrice;
  
  // Calculate total with tax
  const subtotal = pricePerCredit * rechargeForm.credits;
  const taxRate = pricing.tax.percentage || 0;
  const isTaxInclusive = pricing.tax.isInclusive;
  let totalPrice;
  let taxAmount;
  
  if (isTaxInclusive) {
    taxAmount = subtotal * (taxRate / (100 + taxRate));
    totalPrice = subtotal;
  } else {
    taxAmount = subtotal * (taxRate / 100);
    totalPrice = subtotal + taxAmount;
  }

  // Filter alerts based on filters
  const filteredAlerts = alerts.filter(alert => {
    if (alertFilters.type !== 'all' && alert.type !== alertFilters.type) return false;
    if (alertFilters.status !== 'all' && alert.status !== alertFilters.status) return false;
    if (alertFilters.search && !alert.message?.toLowerCase().includes(alertFilters.search.toLowerCase()) && 
        !alert.title?.toLowerCase().includes(alertFilters.search.toLowerCase())) return false;
    return true;
  });

  return (
    <TenantAdminLayout>
      <div className="min-h-screen bg-slate-50 pb-20">
        
        {/* ============================================
            COMPACT HEADER
            ============================================ */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-3 sm:px-4">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <div className="flex items-center gap-2 min-w-0">
                <div className="p-1.5 bg-indigo-50 rounded-lg sm:hidden">
                  <FiMail className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <h1 className="text-sm sm:text-base font-bold text-slate-900 truncate">Messages</h1>
                  <p className="text-[10px] text-slate-400 hidden sm:block">Manage your communication</p>
                </div>
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2">
                <button
                  onClick={() => {
                    setRechargeForm({ 
                      type: 'email', 
                      credits: 100, 
                      paymentMethod: 'card',
                      useBulkPricing: false,
                      selectedBulkTier: null
                    });
                    setShowRechargeModal(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-indigo-600 text-white rounded-lg text-[11px] sm:text-xs font-bold hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  <FiPlus className="w-3.5 h-3.5" />
                  <span className="hidden xs:inline">Add</span> Credits
                </button>
                <button
                  onClick={fetchAllData}
                  disabled={refreshing}
                  className="p-2 sm:p-2.5 bg-slate-100 rounded-lg text-slate-600 hover:bg-slate-200 transition-colors"
                >
                  <FiRefreshCw className={`w-3.5 h-3.5 ${refreshing && 'animate-spin'}`} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================
            MOBILE BOTTOM NAVIGATION
            ============================================ */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30 sm:hidden">
          <div className="flex items-center justify-around px-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center py-2 px-2 min-w-[50px] transition-colors relative ${
                  activeTab === tab.id ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                {tab.icon}
                <span className={`text-[9px] font-medium mt-0.5 ${
                  activeTab === tab.id ? 'text-indigo-600' : 'text-slate-400'
                }`}>
                  {tab.label}
                </span>
                {activeTab === tab.id && (
                  <span className="absolute -top-[1px] left-1/2 -translate-x-1/2 w-8 h-0.5 bg-indigo-600 rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ============================================
            DESKTOP TABS NAVIGATION
            ============================================ */}
        <div className="hidden sm:block bg-white border-b border-slate-200 sticky top-14 z-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex gap-1 overflow-x-auto py-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                    activeTab === tab.id 
                      ? 'bg-indigo-50 text-indigo-700' 
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                  {tab.id === 'alerts' && alertStats.unread > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-[8px] font-bold bg-rose-500 text-white rounded-full">
                      {alertStats.unread}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ============================================
            MAIN CONTENT
            ============================================ */}
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 space-y-3 sm:space-y-4">
          
          {/* OFFER NOTIFICATION BANNER */}
          {(tenantPricing.summary.hasActiveDiscount || tenantPricing.summary.hasActiveBulkPricing) && (
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 rounded-lg text-amber-600 flex-shrink-0">
                  <FiBell className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-bold text-amber-800">Special Offer Available! 🎉</h4>
                  <div className="mt-1 space-y-1">
                    {tenantPricing.overrides
                      .filter(o => o.isCurrentlyActive && o.discountValue > 0)
                      .map((override, idx) => (
                        <p key={idx} className="text-[11px] text-amber-700">
                          <span className="font-bold capitalize">{override.communicationType}</span>
                          : {override.discountType === 'percentage' 
                            ? `${override.discountValue}% discount` 
                            : `${formatPrice(override.discountValue)} off`}
                          {override.validTo && (
                            <span className="text-amber-500 ml-1">
                              (Valid until {formatDate(override.validTo)})
                            </span>
                          )}
                        </p>
                      ))}
                    {tenantPricing.bulkPricing
                      .filter(b => b.isCurrentlyActive)
                      .map((bulk, idx) => (
                        <p key={`bulk-${idx}`} className="text-[11px] text-amber-700">
                          <span className="font-bold capitalize">{bulk.communicationType}</span>
                          : Bulk pricing - {formatPrice(bulk.specialPrice)}/credit for {bulk.minCredits}+ credits
                          {bulk.validTo && (
                            <span className="text-amber-500 ml-1">
                              (Valid until {formatDate(bulk.validTo)})
                            </span>
                          )}
                        </p>
                      ))}
                  </div>
                  <button
                    onClick={() => {
                      setRechargeForm({ 
                        type: 'email', 
                        credits: 500, 
                        paymentMethod: 'card',
                        useBulkPricing: true,
                        selectedBulkTier: null
                      });
                      setShowRechargeModal(true);
                    }}
                    className="mt-2 text-[10px] font-bold text-amber-800 hover:text-amber-900 underline"
                  >
                    Buy credits with discounts now →
                  </button>
                </div>
                <button 
                  onClick={() => setTenantPricing(prev => ({ ...prev, summary: { ...prev.summary, hasActiveDiscount: false, hasActiveBulkPricing: false } }))}
                  className="text-amber-400 hover:text-amber-600 flex-shrink-0"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* MASTER TOGGLE */}
          <div className={`p-3 sm:p-4 rounded-xl border transition-all ${
            settings.communicationEnabled 
              ? 'bg-white border-slate-200' 
              : 'bg-rose-50 border-rose-200'
          }`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs sm:text-sm font-bold text-slate-900">System Status</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                    settings.communicationEnabled 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-rose-100 text-rose-700'
                  }`}>
                    {settings.communicationEnabled ? 'Active' : 'Paused'}
                  </span>
                </div>
                <p className="text-[10px] sm:text-xs text-slate-500 truncate">
                  {settings.communicationEnabled 
                    ? 'Messages are being delivered' 
                    : 'All messaging is paused'}
                </p>
              </div>
              <button 
                onClick={() => handleToggleCommunication('master')} 
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                  settings.communicationEnabled ? 'bg-indigo-600' : 'bg-slate-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.communicationEnabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>

          {/* SYSTEM OFF STATE */}
          {!settings.communicationEnabled ? (
            <div className="text-center py-8 sm:py-12 bg-white border border-slate-200 rounded-xl">
              <div className="p-2.5 bg-rose-50 text-rose-600 rounded-full w-fit mx-auto mb-2">
                <FiAlertCircle className="w-5 h-5" />
              </div>
              <h2 className="text-sm font-bold text-slate-900">Messaging is Paused</h2>
              <p className="text-xs text-slate-500 max-w-xs mx-auto mt-1">
                Enable the system above to manage settings and view logs.
              </p>
            </div>
          ) : (
            <>
              {/* ============================================
                  BALANCE CARDS WITH TAX INFO
                  ============================================ */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
                        <FiMail className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-[10px] font-bold text-slate-700">Email</span>
                    </div>
                    <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold ${
                      settings.emailEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'
                    }`}>
                      {settings.emailEnabled ? 'On' : 'Off'}
                    </span>
                  </div>
                  <div>
                    <span className="text-lg sm:text-xl font-black text-slate-900">{formatNumber(wallet.emailCredits)}</span>
                    <span className="text-[10px] text-slate-400 ml-1">credits</span>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <div className="p-1.5 bg-purple-50 text-purple-600 rounded-lg">
                        <FiMessageSquare className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-[10px] font-bold text-slate-700">SMS</span>
                    </div>
                    <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold ${
                      settings.smsEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'
                    }`}>
                      {settings.smsEnabled ? 'On' : 'Off'}
                    </span>
                  </div>
                  <div>
                    <span className="text-lg sm:text-xl font-black text-slate-900">{formatNumber(wallet.smsCredits)}</span>
                    <span className="text-[10px] text-slate-400 ml-1">credits</span>
                  </div>
                </div>
              </div>

              {/* TAX INFO CARD */}
              <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-amber-50 text-amber-600 rounded-lg">
                      <FiPercent className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                          pricing.tax.isInclusive ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {pricing.tax.isInclusive ? 'Inclusive ✅' : 'Exclusive ➕'}
                        </span>
                        <span className="text-[8px] text-slate-300 capitalize">({pricing.tax.source})</span>
                      </div>
                    </div>
                  </div>
                  <FiInfo className="w-4 h-4 text-slate-400" title="Tax applied to purchases" />
                </div>
              </div>

              {/* ============================================
                  TAB CONTENT
                  ============================================ */}
              
              {/* DASHBOARD */}
              {activeTab === 'dashboard' && (
                <div className="space-y-3 sm:space-y-4">
                  <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <p className="text-[10px] text-slate-500">Sent</p>
                        <p className="text-sm font-bold text-slate-900">
                          {formatNumber(wallet.totalEmailsSent + wallet.totalSmsSent)}
                        </p>
                      </div>
                      <div className="text-center border-x border-slate-100">
                        <p className="text-[10px] text-slate-500">Email Used</p>
                        <p className="text-sm font-bold text-slate-900">{formatNumber(wallet.emailTotalUsed)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] text-slate-500">SMS Used</p>
                        <p className="text-sm font-bold text-slate-900">{formatNumber(wallet.smsTotalUsed)}</p>
                      </div>
                    </div>
                  </div>



                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <button
                      onClick={() => {
                        setRechargeForm({ 
                          type: 'email', 
                          credits: 100, 
                          paymentMethod: 'card',
                          useBulkPricing: false,
                          selectedBulkTier: null
                        });
                        setShowRechargeModal(true);
                      }}
                      className="p-3 sm:p-4 bg-white border border-slate-200 rounded-xl text-center hover:bg-slate-50 transition-colors"
                    >
                      <FiPlus className="w-4 h-4 text-indigo-600 mx-auto mb-1" />
                      <p className="text-[10px] font-bold text-slate-700">Add Credits</p>
                    </button>
                    <button
                      onClick={() => setActiveTab('bundles')}
                      className="p-3 sm:p-4 bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-xl text-center text-white hover:shadow-md transition-shadow"
                    >
                      <FiGift className="w-4 h-4 mx-auto mb-1 opacity-90" />
                      <p className="text-[10px] font-bold">Get Package</p>
                    </button>
                  </div>

                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-100 flex items-center justify-between">
                      <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Recent Activity</h3>
                      <button 
                        onClick={() => setActiveTab('logs')} 
                        className="text-[10px] font-bold text-indigo-600 flex items-center gap-0.5"
                      >
                        View All <FiChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {logs.slice(0, 3).map((log) => (
                        <div 
                          key={log.id} 
                          onClick={() => setSelectedLog(log)} 
                          className="px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`p-1.5 rounded-lg shrink-0 ${
                              log.type === 'email' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
                            }`}>
                              {log.type === 'email' ? <FiMail className="w-3 h-3" /> : <FiMessageSquare className="w-3 h-3" />}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-slate-900 truncate">{log.recipient}</p>
                              <p className="text-[10px] text-slate-400 truncate">{log.subject || log.useCase}</p>
                            </div>
                          </div>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold shrink-0 ml-2 ${getStatusStyle(log.status)}`}>
                            {log.status}
                          </span>
                        </div>
                      ))}
                      {logs.length === 0 && (
                        <p className="text-center py-6 text-xs text-slate-400">No recent activity</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* SETTINGS - Only Communication Settings */}
              {activeTab === 'settings' && (
                <div className="space-y-3 sm:space-y-4">
                  {/* Existing Channels Section */}
                  <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 space-y-3">
                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Channels</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
                            <FiMail className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <p className="text-xs font-bold text-slate-800">Email</p>
                            <p className="text-[9px] text-slate-500">Allow emails</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => handleToggleCommunication('email')} 
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                            settings.emailEnabled ? 'bg-indigo-600' : 'bg-slate-300'
                          }`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            settings.emailEnabled ? 'translate-x-4.5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-purple-50 text-purple-600 rounded-lg">
                            <FiMessageSquare className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <p className="text-xs font-bold text-slate-800">SMS</p>
                            <p className="text-[9px] text-slate-500">Allow texts</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => handleToggleCommunication('sms')} 
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                            settings.smsEnabled ? 'bg-indigo-600' : 'bg-slate-300'
                          }`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            settings.smsEnabled ? 'translate-x-4.5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Existing Message Types Section */}
                  <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Message Types</h3>
                        <p className="text-[10px] text-slate-500 mt-0.5">Select allowed notification types</p>
                      </div>
                      {hasUnsavedChanges() && (
                        <div className="flex gap-1.5">
                          <button
                            onClick={handleResetUseCases}
                            className="px-2.5 py-1 text-[10px] font-bold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
                          >
                            Undo
                          </button>
                          <button
                            onClick={handleSaveUseCases}
                            disabled={savingUseCases}
                            className="px-3 py-1 text-[10px] font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-1"
                          >
                            <FiSave className="w-3 h-3" />
                            {savingUseCases ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-[10px] font-bold text-slate-600 flex items-center gap-1.5">
                            <FiMail className="w-3 h-3 text-blue-500" /> Email
                          </p>
                          <span className="text-[9px] text-slate-400">{localEmailUseCases.length} selected</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(allEmailUseCaseLabels).map(([key, label]) => (
                            <label 
                              key={key} 
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-medium cursor-pointer transition-all ${
                                localEmailUseCases.includes(key)
                                  ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                  : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={localEmailUseCases.includes(key)}
                                onChange={() => handleLocalEmailUseCaseChange(key)}
                                disabled={!settings.communicationEnabled || !settings.emailEnabled}
                                className="w-3 h-3 text-indigo-600 rounded focus:ring-indigo-500 disabled:opacity-40"
                              />
                              <span>{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-[10px] font-bold text-slate-600 flex items-center gap-1.5">
                            <FiMessageSquare className="w-3 h-3 text-purple-500" /> SMS
                          </p>
                          <span className="text-[9px] text-slate-400">{localSmsUseCases.length} selected</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(allSmsUseCaseLabels).map(([key, label]) => (
                            <label 
                              key={key} 
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-medium cursor-pointer transition-all ${
                                localSmsUseCases.includes(key)
                                  ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                  : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={localSmsUseCases.includes(key)}
                                onChange={() => handleLocalSmsUseCaseChange(key)}
                                disabled={!settings.communicationEnabled || !settings.smsEnabled}
                                className="w-3 h-3 text-indigo-600 rounded focus:ring-indigo-500 disabled:opacity-40"
                              />
                              <span>{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    {hasUnsavedChanges() && (
                      <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-[10px] text-amber-700">
                        <FiAlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>Unsaved changes. Click "Save" to apply.</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ALERTS TAB - List of Alerts */}
              {activeTab === 'alerts' && (
                <div className="space-y-3 sm:space-y-4">
                  {/* Alert Stats Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                    <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                      <p className="text-[10px] text-slate-500">Total</p>
                      <p className="text-lg font-black text-slate-900">{alertStats.total}</p>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                      <p className="text-[10px] text-slate-500">Unread</p>
                      <p className="text-lg font-black text-rose-600">{alertStats.unread}</p>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                      <p className="text-[10px] text-slate-500">Pending</p>
                      <p className="text-lg font-black text-amber-600">{alertStats.pending}</p>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                      <p className="text-[10px] text-slate-500">Resolved</p>
                      <p className="text-lg font-black text-emerald-600">{alertStats.resolved}</p>
                    </div>
                  </div>

                  {/* Alert Filters */}
                  <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative flex-1">
                        <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                        <input
                          type="text"
                          placeholder="Search alerts..."
                          value={alertFilters.search}
                          onChange={(e) => setAlertFilters({...alertFilters, search: e.target.value})}
                          className="w-full pl-8 pr-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <select
                        value={alertFilters.type}
                        onChange={(e) => setAlertFilters({...alertFilters, type: e.target.value})}
                        className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium focus:outline-none"
                      >
                        <option value="all">All Types</option>
                        <option value="email">Email</option>
                        <option value="sms">SMS</option>
                        <option value="attendance">Attendance</option>
                        <option value="fee">Fee</option>
                        <option value="system">System</option>
                      </select>
                      <select
                        value={alertFilters.status}
                        onChange={(e) => setAlertFilters({...alertFilters, status: e.target.value})}
                        className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium focus:outline-none"
                      >
                        <option value="all">All Status</option>
                        <option value="unread">Unread</option>
                        <option value="read">Read</option>
                        <option value="pending">Pending</option>
                        <option value="resolved">Resolved</option>
                      </select>
                      <select
                        value={alertFilters.dateRange}
                        onChange={(e) => setAlertFilters({...alertFilters, dateRange: e.target.value})}
                        className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium focus:outline-none"
                      >
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="all">All Time</option>
                      </select>
                    </div>
                  </div>

                  {/* Alerts List */}
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-100 flex items-center justify-between">
                      <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        {filteredAlerts.length} Alerts
                      </h3>
                      {filteredAlerts.length > 0 && (
                        <button
                          onClick={() => {
                            filteredAlerts.forEach(alert => {
                              if (alert.status === 'unread' || alert.read === false) {
                                handleMarkAlertRead(alert.id);
                              }
                            });
                          }}
                          className="text-[10px] font-bold text-indigo-600 hover:text-indigo-700"
                        >
                          Mark All Read
                        </button>
                      )}
                    </div>
                    
                    <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                      {filteredAlerts.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="p-3 bg-slate-50 rounded-full w-fit mx-auto mb-3">
                            <FiBell className="w-6 h-6 text-slate-300" />
                          </div>
                          <p className="text-xs text-slate-400">No alerts found</p>
                          <p className="text-[10px] text-slate-300 mt-0.5">Try adjusting your filters</p>
                        </div>
                      ) : (
                        filteredAlerts.map((alert) => (
                          <div 
                            key={alert.id} 
                            className={`px-3 sm:px-4 py-2.5 sm:py-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                              alert.status === 'unread' || alert.read === false ? 'bg-indigo-50/30' : ''
                            }`}
                            onClick={() => {
                              setSelectedAlert(alert);
                              setShowAlertModal(true);
                              if (alert.status === 'unread' || alert.read === false) {
                                handleMarkAlertRead(alert.id);
                              }
                            }}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-3 min-w-0 flex-1">
                                <div className={`p-1.5 rounded-lg shrink-0 mt-0.5 bg-${getAlertColor(alert.type)}-50 text-${getAlertColor(alert.type)}-600`}>
                                  {getAlertIcon(alert.type)}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className={`text-xs font-bold truncate ${
                                      alert.status === 'unread' || alert.read === false ? 'text-slate-900' : 'text-slate-600'
                                    }`}>
                                      {alert.title || 'Alert'}
                                    </p>
                                    {alert.status === 'unread' && (
                                      <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full flex-shrink-0" />
                                    )}
                                  </div>
                                  <p className="text-[10px] text-slate-500 truncate">{alert.message}</p>
                                  <div className="flex items-center gap-3 mt-0.5">
                                    <span className="text-[9px] text-slate-400 flex items-center gap-0.5">
                                      <FiClock className="w-2.5 h-2.5" />
                                      {formatDate(alert.createdAt || alert.sentAt)}
                                    </span>
                                    <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold ${getStatusStyle(alert.status)}`}>
                                      {alert.status || 'pending'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {(alert.status === 'unread' || alert.read === false) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMarkAlertRead(alert.id);
                                    }}
                                    className="p-1 text-slate-400 hover:text-indigo-600 transition-colors"
                                    title="Mark as read"
                                  >
                                    <FiEye className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {alert.status !== 'resolved' && alert.status !== 'read' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleResolveAlert(alert.id);
                                    }}
                                    className="p-1 text-slate-400 hover:text-emerald-600 transition-colors"
                                    title="Resolve"
                                  >
                                    <FiCheck className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteAlert(alert.id);
                                  }}
                                  className="p-1 text-slate-400 hover:text-rose-600 transition-colors"
                                  title="Delete"
                                >
                                  <FiX className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ALERT CONFIG TAB - AlertSettings Component */}
              {activeTab === 'alert-config' && (
                <AlertSettings 
                  tenantId={tenantId}
                  onSettingsChange={(config) => {
                    console.log('Alert settings updated:', config);
                  }}
                />
              )}

              {/* ANALYTICS TAB - Detailed Usage */}
              {activeTab === 'analytics' && (
                <div className="space-y-3 sm:space-y-4">
                  {analyticsLoading ? (
                    <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent mx-auto mb-3" />
                      <p className="text-xs text-slate-500">Loading analytics...</p>
                    </div>
                  ) : (
                    <>
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                        <div className="bg-white border border-slate-200 rounded-xl p-3">
                          <p className="text-[10px] text-slate-500">Total Sent</p>
                          <p className="text-lg font-black text-slate-900">
                            {formatNumber(detailedUsage?.summary?.totalEmails || 0)}
                          </p>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl p-3">
                          <p className="text-[10px] text-slate-500">Credits Used</p>
                          <p className="text-lg font-black text-indigo-600">
                            {formatNumber(detailedUsage?.summary?.totalCreditsUsed || 0)}
                          </p>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl p-3">
                          <p className="text-[10px] text-slate-500">Unique Recipients</p>
                          <p className="text-lg font-black text-slate-900">
                            {formatNumber(detailedUsage?.summary?.uniqueRecipients || 0)}
                          </p>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl p-3">
                          <p className="text-[10px] text-slate-500">Success Rate</p>
                          <p className="text-lg font-black text-emerald-600">
                            {detailedUsage?.summary?.totalEmails > 0 
                              ? `${Math.round((detailedUsage.summary.successfulDeliveries / detailedUsage.summary.totalEmails) * 100)}%`
                              : '0%'}
                          </p>
                        </div>
                      </div>

                      {/* Status Breakdown */}
                      <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
                        <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Status Breakdown</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {(detailedUsage?.breakdowns?.byStatus || []).map((item) => (
                            <div key={item._id} className="bg-slate-50 rounded-lg p-2 text-center">
                              <p className={`text-[10px] font-bold ${
                                item._id === 'delivered' ? 'text-emerald-600' : 
                                item._id === 'failed' ? 'text-rose-600' : 
                                item._id === 'pending' ? 'text-amber-600' : 'text-slate-600'
                              }`}>
                                {item._id || 'unknown'}
                              </p>
                              <p className="text-sm font-black text-slate-900">{item.count}</p>
                              <p className="text-[8px] text-slate-400">{item.creditsUsed} credits</p>
                            </div>
                          ))}
                          {(!detailedUsage?.breakdowns?.byStatus || detailedUsage.breakdowns.byStatus.length === 0) && (
                            <div className="col-span-4 text-center py-4 text-xs text-slate-400">
                              No status data available
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Use Case Breakdown */}
                      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                        <div className="px-3 sm:px-4 py-2.5 border-b border-slate-100">
                          <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Usage by Use Case</h3>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {(detailedUsage?.breakdowns?.byUseCase || []).map((item) => (
                            <div key={item._id} className="px-3 sm:px-4 py-2.5 flex items-center justify-between">
                              <div>
                                <p className="text-xs font-bold text-slate-800 capitalize">{item._id || 'Unknown'}</p>
                                <div className="flex gap-3 mt-0.5">
                                  <span className="text-[9px] text-slate-400">{item.totalEmails} emails</span>
                                  <span className="text-[9px] text-emerald-600">{item.successfulDeliveries} delivered</span>
                                  {item.failedDeliveries > 0 && (
                                    <span className="text-[9px] text-rose-600">{item.failedDeliveries} failed</span>
                                  )}
                                </div>
                              </div>
                              <span className="text-xs font-bold text-indigo-600">{item.totalCreditsUsed} credits</span>
                            </div>
                          ))}
                          {(!detailedUsage?.breakdowns?.byUseCase || detailedUsage.breakdowns.byUseCase.length === 0) && (
                            <p className="text-center py-6 text-xs text-slate-400">No usage data available</p>
                          )}
                        </div>
                      </div>

                      {/* Daily Trend */}
                      <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
                        <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Daily Trend (Last 30 Days)</h3>
                        <div className="space-y-1">
                          {(detailedUsage?.breakdowns?.byDay || []).slice(0, 10).map((item) => (
                            <div key={item._id.date} className="flex items-center gap-2">
                              <span className="text-[10px] text-slate-500 w-24 flex-shrink-0">{item._id.date}</span>
                              <div className="flex-1 h-6 bg-slate-100 rounded-lg overflow-hidden flex">
                                <div 
                                  className="h-full bg-emerald-500 transition-all"
                                  style={{ 
                                    width: `${Math.min((item.successfulDeliveries / (item.totalEmails || 1)) * 100, 100)}%` 
                                  }}
                                />
                                <div 
                                  className="h-full bg-rose-400 transition-all"
                                  style={{ 
                                    width: `${Math.min((item.failedDeliveries / (item.totalEmails || 1)) * 100, 100)}%` 
                                  }}
                                />
                              </div>
                              <span className="text-[10px] font-bold text-slate-700 w-12 text-right flex-shrink-0">
                                {item.totalEmails}
                              </span>
                            </div>
                          ))}
                          {(!detailedUsage?.breakdowns?.byDay || detailedUsage.breakdowns.byDay.length === 0) && (
                            <p className="text-center py-6 text-xs text-slate-400">No daily data available</p>
                          )}
                        </div>
                      </div>

                      {/* Top Recipients */}
                      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                        <div className="px-3 sm:px-4 py-2.5 border-b border-slate-100">
                          <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Top Recipients</h3>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {(detailedUsage?.breakdowns?.byRecipient || []).slice(0, 10).map((item) => (
                            <div key={item._id} className="px-3 sm:px-4 py-2.5 flex items-center justify-between">
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-bold text-slate-800 truncate">{item.recipientName || item._id}</p>
                                <p className="text-[9px] text-slate-400 truncate">{item._id}</p>
                              </div>
                              <div className="flex items-center gap-3 shrink-0 ml-2">
                                <span className="text-[9px] text-slate-500">{item.totalEmails} sent</span>
                                <span className="text-[10px] font-bold text-indigo-600">{item.totalCreditsUsed} credits</span>
                              </div>
                            </div>
                          ))}
                          {(!detailedUsage?.breakdowns?.byRecipient || detailedUsage.breakdowns.byRecipient.length === 0) && (
                            <p className="text-center py-6 text-xs text-slate-400">No recipient data available</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* LOGS */}
              {activeTab === 'logs' && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="p-3 sm:p-4 bg-slate-50 border-b border-slate-200 space-y-2">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative flex-1">
                        <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                        <input
                          type="text"
                          placeholder="Search..."
                          value={filters.search}
                          onChange={(e) => setFilters({...filters, search: e.target.value})}
                          className="w-full pl-8 pr-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <select
                        value={filters.type}
                        onChange={(e) => setFilters({...filters, type: e.target.value})}
                        className="px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium focus:outline-none"
                      >
                        <option value="all">All Types</option>
                        <option value="email">Email</option>
                        <option value="sms">SMS</option>
                      </select>
                      <select
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                        className="px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium focus:outline-none"
                      >
                        <option value="all">All Status</option>
                        <option value="sent">Sent</option>
                        <option value="delivered">Delivered</option>
                        <option value="failed">Failed</option>
                      </select>
                      <button
                        onClick={handleExportLogs}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50"
                      >
                        <FiDownload className="w-3.5 h-3.5" /> Export
                      </button>
                    </div>
                  </div>

                  <div className="divide-y divide-slate-100">
                    {logs.map((log) => (
                      <div 
                        key={log.id} 
                        onClick={() => setSelectedLog(log)} 
                        className="px-3 sm:px-4 py-2.5 sm:py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`p-1.5 rounded-lg shrink-0 ${
                              log.type === 'email' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
                            }`}>
                              {log.type === 'email' ? <FiMail className="w-3 h-3" /> : <FiMessageSquare className="w-3 h-3" />}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-slate-900 truncate">{log.recipient}</p>
                              <p className="text-[10px] text-slate-400 truncate">{log.subject || log.useCase}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${getStatusStyle(log.status)}`}>
                              {log.status}
                            </span>
                            <span className="text-[9px] text-slate-400 hidden xs:block">{formatDate(log.sentAt)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {logs.length === 0 && (
                      <p className="text-center py-8 text-xs text-slate-400">No logs found</p>
                    )}
                  </div>
                </div>
              )}

              {/* WALLET / TRANSACTIONS */}
              {activeTab === 'wallet' && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-100">
                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Payment History</h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {transactions.map((t) => (
                      <div key={t.id} className="px-3 sm:px-4 py-2.5 sm:py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-slate-800">{t.description}</p>
                            <p className="text-[10px] text-slate-400">{formatDate(t.createdAt)}</p>
                            {t.taxName && (
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[8px] text-slate-400">Tax:</span>
                                <span className="text-[8px] font-bold text-slate-600">{t.taxName}</span>
                                <span className="text-[8px] text-slate-400">@ {t.taxPercentage || 0}%</span>
                                <span className={`text-[7px] px-1 py-0.5 rounded-full font-bold ${
                                  t.taxType === 'inclusive' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'
                                }`}>
                                  {t.taxType || 'inclusive'}
                                </span>
                                {t.taxAmount && (
                                  <span className="text-[8px] text-amber-600">
                                    Tax: {formatPrice(t.taxAmount)}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <span className={`text-xs font-bold shrink-0 ml-2 ${
                            t.amount > 0 ? 'text-emerald-600' : 'text-rose-600'
                          }`}>
                            {t.amount > 0 ? `+${t.amount}` : t.amount}
                          </span>
                        </div>
                        {t.totalAmountPaid && (
                          <div className="text-right text-[9px] text-slate-400 mt-0.5">
                            Total: {formatPrice(t.totalAmountPaid)}
                          </div>
                        )}
                      </div>
                    ))}
                    {transactions.length === 0 && (
                      <p className="text-center py-8 text-xs text-slate-400">No transactions</p>
                    )}
                  </div>
                </div>
              )}

              {/* BUNDLES WITH TAX INFO */}
              {activeTab === 'bundles' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {bundles.map((bundle) => (
                    <div key={bundle._id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="text-2xl">{bundle.icon || '📦'}</span>
                          <h4 className="text-xs font-bold text-slate-900 mt-0.5">{bundle.name}</h4>
                        </div>
                        <span className="text-sm font-black text-slate-900">{formatPrice(bundle.price)}</span>
                      </div>
                      <p className="text-[10px] text-slate-500 mb-2 line-clamp-2">{bundle.description}</p>
                      
                      {/* Tax Info */}
                      {bundle.priceBreakdown?.tax && (
                        <div className="text-[9px] text-slate-400 mb-2 p-1.5 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-bold text-slate-600">{bundle.priceBreakdown.tax.name}</span>
                            <span className="text-slate-400">@</span>
                            <span className="font-bold">{bundle.priceBreakdown.tax.percentage}%</span>
                            <span className="text-slate-300">•</span>
                            <span className={bundle.priceBreakdown.tax.type === 'inclusive' ? 'text-green-600 font-bold' : 'text-blue-600 font-bold'}>
                              {bundle.priceBreakdown.tax.type === 'inclusive' ? '✅ Included' : '➕ Added'}
                            </span>
                            <span className="text-slate-300">•</span>
                            <span className="text-slate-400 text-[8px] capitalize">Source: {bundle.priceBreakdown.tax.source}</span>
                          </div>
                          {bundle.priceBreakdown.tax.amount > 0 && (
                            <div className="text-[8px] text-amber-600 mt-0.5">
                              Tax Amount: {formatPrice(bundle.priceBreakdown.tax.amount)}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex gap-1.5 text-[9px] font-bold mb-3">
                        <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded">📧 {bundle.emailCredits}</span>
                        <span className="text-purple-600 bg-purple-50 px-2 py-0.5 rounded">📱 {bundle.smsCredits}</span>
                      </div>
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => setSelectedBundleDetails(bundle)}
                          className="flex-1 py-1.5 text-[10px] font-bold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
                        >
                          Details
                        </button>
                        <RazorpayCheckout
                          paymentType="bundle"
                          bundleId={bundle._id}
                          onSuccess={(data) => {
                            console.log('✅ Bundle purchase success:', data);
                            fetchAllData();
                            triggerAlert('Success', 'Bundle purchased successfully!', 'success');
                          }}
                          onFailure={(error) => {
                            console.log('❌ Bundle purchase failed:', error);
                            triggerAlert('Purchase Failed', error.message || 'Could not purchase bundle.', 'error');
                          }}
                          onClose={() => {}}
                        >
                          <button className="flex-1 py-1.5 text-[10px] font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                            Buy
                          </button>
                        </RazorpayCheckout>
                      </div>
                    </div>
                  ))}
                  {bundles.length === 0 && (
                    <div className="col-span-full text-center py-8 text-xs text-slate-400 bg-white border border-slate-200 rounded-xl">
                      No packages available
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ============================================
            ALERT DETAIL MODAL
            ============================================ */}
        {showAlertModal && selectedAlert && (
          <div className="fixed inset-0 bg-slate-900/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white">
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg bg-${getAlertColor(selectedAlert.type)}-50 text-${getAlertColor(selectedAlert.type)}-600`}>
                    {getAlertIcon(selectedAlert.type)}
                  </div>
                  <h3 className="text-sm font-bold text-slate-900">{selectedAlert.title || 'Alert Details'}</h3>
                </div>
                <button onClick={() => {
                  setShowAlertModal(false);
                  setSelectedAlert(null);
                }} className="p-1 bg-slate-100 rounded-full hover:bg-slate-200">
                  <FiX className="w-4 h-4 text-slate-500" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Alert Status */}
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${getStatusStyle(selectedAlert.status)}`}>
                    {selectedAlert.status || 'pending'}
                  </span>
                  <span className="text-[10px] text-slate-400 flex items-center gap-1">
                    <FiClock className="w-3 h-3" />
                    {formatDate(selectedAlert.createdAt || selectedAlert.sentAt)}
                  </span>
                  {selectedAlert.type && (
                    <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full font-medium">
                      {selectedAlert.type}
                    </span>
                  )}
                </div>

                {/* Alert Message */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedAlert.message}</p>
                </div>

                {/* Additional Details */}
                {selectedAlert.details && (
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Additional Details</h4>
                    <div className="space-y-1 text-xs text-slate-600">
                      {Object.entries(selectedAlert.details).map(([key, value]) => (
                        <div key={key} className="flex justify-between py-0.5 border-b border-slate-100 last:border-0">
                          <span className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}</span>
                          <span className="font-medium text-slate-700">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                  {selectedAlert.status !== 'resolved' && selectedAlert.status !== 'read' && (
                    <button
                      onClick={() => {
                        handleResolveAlert(selectedAlert.id);
                        setShowAlertModal(false);
                        setSelectedAlert(null);
                      }}
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 transition-colors flex items-center gap-1"
                    >
                      <FiCheck className="w-3.5 h-3.5" /> Resolve
                    </button>
                  )}
                  <button
                    onClick={() => {
                      handleDeleteAlert(selectedAlert.id);
                      setShowAlertModal(false);
                      setSelectedAlert(null);
                    }}
                    className="px-4 py-2 bg-rose-600 text-white rounded-lg text-xs font-bold hover:bg-rose-700 transition-colors flex items-center gap-1"
                  >
                    <FiX className="w-3.5 h-3.5" /> Delete
                  </button>
                  <button
                    onClick={() => {
                      setShowAlertModal(false);
                      setSelectedAlert(null);
                    }}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-xs font-bold hover:bg-slate-200 transition-colors ml-auto"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================
            BUY CREDITS MODAL WITH TAX INFO
            ============================================ */}
        {showRechargeModal && (
          <div className="fixed inset-0 bg-slate-900/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white">
                <h3 className="text-xs font-bold text-slate-900">Buy Credits</h3>
                <button 
                  onClick={() => {
                    setShowRechargeModal(false);
                    setRechargeForm({
                      type: 'email',
                      credits: 100,
                      paymentMethod: 'card',
                      useBulkPricing: false,
                      selectedBulkTier: null
                    });
                  }} 
                  className="p-1 bg-slate-100 rounded-full hover:bg-slate-200"
                >
                  <FiX className="w-4 h-4 text-slate-500" />
                </button>
              </div>
              
              <div className="p-4 space-y-3 text-xs">
                {/* Show active discounts */}
                {tenantPricing.overrides
                  .filter(o => o.isCurrentlyActive && o.discountValue > 0)
                  .map((override, idx) => (
                    <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-2.5">
                      <div className="flex items-center gap-2">
                        <FiPercent className="text-green-600 w-4 h-4" />
                        <span className="text-xs font-bold text-green-700">
                          {override.communicationType === 'email' ? '📧 Email' : '📱 SMS'}: 
                          {override.discountType === 'percentage' 
                            ? ` ${override.discountValue}% discount` 
                            : ` ${formatPrice(override.discountValue)} off`}
                        </span>
                      </div>
                      {override.validTo && (
                        <p className="text-[10px] text-green-600 ml-6">
                          Valid until {formatDate(override.validTo)}
                        </p>
                      )}
                    </div>
                  ))}
                
                {/* Channel Selection */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Channel</label>
                  <select
                    value={rechargeForm.type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setRechargeForm({
                        ...rechargeForm,
                        type: newType,
                        useBulkPricing: false,
                        selectedBulkTier: null
                      });
                    }}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="email">Email Credits</option>
                    <option value="sms">SMS Credits</option>
                  </select>
                </div>
                
                {/* Credit Amount */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Credits Required</label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={rechargeForm.credits}
                    onChange={(e) => {
                      const credits = parseInt(e.target.value) || 0;
                      const bestBulk = getBestBulkPrice(rechargeForm.type, credits);
                      setRechargeForm({
                        ...rechargeForm,
                        credits: credits,
                        useBulkPricing: !!bestBulk && credits >= (bestBulk.minCredits || 0),
                        selectedBulkTier: bestBulk ? bestBulk._id : null
                      });
                    }}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                {/* Bulk Pricing Options */}
                {hasBulkPricing && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <FiTrendingUp className="text-purple-600 w-4 h-4" />
                      <span className="text-xs font-bold text-purple-700">Bulk Pricing Available</span>
                    </div>
                    
                    <div className="space-y-2">
                      {activeBulkPricing.map((bulk, idx) => {
                        const isQualified = rechargeForm.credits >= bulk.minCredits;
                        const isSelected = rechargeForm.selectedBulkTier === bulk._id;
                        
                        return (
                          <div 
                            key={idx}
                            className={`border rounded-lg p-2.5 transition-all cursor-pointer ${
                              isQualified && isSelected
                                ? 'border-purple-500 bg-purple-100'
                                : isQualified
                                ? 'border-purple-300 bg-purple-50/50 hover:bg-purple-100'
                                : 'border-slate-200 bg-white opacity-60'
                            }`}
                            onClick={() => {
                              if (isQualified) {
                                setRechargeForm({
                                  ...rechargeForm,
                                  useBulkPricing: true,
                                  selectedBulkTier: bulk._id
                                });
                              }
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-bold text-slate-800">{bulk.minCredits}+ credits</span>
                                <span className="text-green-600 font-bold ml-2">
                                  {formatPrice(bulk.specialPrice)}/credit
                                </span>
                              </div>
                              {isQualified && (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="radio"
                                    checked={isSelected && rechargeForm.useBulkPricing}
                                    onChange={() => {
                                      setRechargeForm({
                                        ...rechargeForm,
                                        useBulkPricing: true,
                                        selectedBulkTier: bulk._id
                                      });
                                    }}
                                    className="w-3.5 h-3.5 text-purple-600 focus:ring-purple-500"
                                  />
                                  <span className="text-[9px] font-bold text-purple-600">Select</span>
                                </div>
                              )}
                              {!isQualified && (
                                <span className="text-[9px] text-slate-400">
                                  Need {bulk.minCredits - rechargeForm.credits} more
                                </span>
                              )}
                            </div>
                            {bulk.validTo && (
                              <p className="text-[9px] text-purple-500 mt-0.5">
                                Valid until {formatDate(bulk.validTo)}
                              </p>
                            )}
                            {isQualified && (
                              <p className="text-[9px] text-green-600 mt-0.5 font-bold">
                                ✓ You qualify for this bulk rate!
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Price Breakdown with Tax */}
                <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-[10px] text-slate-500">
                  <div className="flex justify-between">
                    <span>Base price per credit:</span>
                    <b className="text-slate-800">
                      {formatPrice(rechargeForm.type === 'email' ? pricing.email.price : pricing.sms.price)}
                    </b>
                  </div>
                  
                  {/* Show discount if applicable */}
                  {(() => {
                    const discount = rechargeForm.type === 'email' ? pricing.email.discount : pricing.sms.discount;
                    if (discount > 0) {
                      return (
                        <div className="flex justify-between text-green-600">
                          <span>Discount Applied:</span>
                          <b>{rechargeForm.type === 'email' 
                            ? (pricing.email.discountType === 'percentage' 
                              ? `${pricing.email.discount}%` 
                              : formatPrice(pricing.email.discount))
                            : (pricing.sms.discountType === 'percentage' 
                              ? `${pricing.sms.discount}%` 
                              : formatPrice(pricing.sms.discount))}
                          </b>
                        </div>
                      );
                    }
                  })()}
                  
                  {/* Show bulk pricing if selected */}
                  {rechargeForm.useBulkPricing && effectivePriceInfo.isBulk && (
                    <div className="flex justify-between text-purple-700 font-bold">
                      <span>Bulk Price:</span>
                      <span>{formatPrice(effectivePriceInfo.price)}/credit</span>
                    </div>
                  )}
                  
                  {/* Show savings */}
                  {rechargeForm.useBulkPricing && effectivePriceInfo.savings > 0 && (
                    <div className="flex justify-between text-emerald-600 font-bold">
                      <span>You Save:</span>
                      <span>{formatPrice(effectivePriceInfo.savings)}</span>
                    </div>
                  )}
                  
                  {/* TAX SECTION */}
                  <div className="border-t border-slate-200 pt-1.5 mt-1">
                    <div className="flex justify-between">
                      <span className="font-medium text-slate-600">
                        {pricing.tax.name} @ {pricing.tax.percentage}%:
                      </span>
                      <b className={pricing.tax.isInclusive ? 'text-green-600' : 'text-blue-600'}>
                        {pricing.tax.isInclusive ? '✅ Included' : '➕ Added'}
                      </b>
                    </div>
                    {taxAmount > 0 && (
                      <div className="flex justify-between text-amber-600 text-[9px]">
                        <span>Tax Amount:</span>
                        <span>{formatPrice(taxAmount)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t border-slate-200 pt-1.5 flex justify-between text-xs font-black text-slate-900">
                    <span>Total:</span>
                    <span className="text-indigo-600">
                      {formatPrice(totalPrice)}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 pt-1">
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowRechargeModal(false);
                      setRechargeForm({
                        type: 'email',
                        credits: 100,
                        paymentMethod: 'card',
                        useBulkPricing: false,
                        selectedBulkTier: null
                      });
                    }} 
                    className="w-full py-2.5 border border-slate-200 text-slate-600 font-bold rounded-lg hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <RazorpayCheckout
                    paymentType="credit"
                    communicationType={rechargeForm.type}
                    credits={rechargeForm.credits}
                    onSuccess={(data) => {
                      console.log('✅ Payment success:', data);
                      fetchAllData();
                      setTimeout(() => {
                        setShowRechargeModal(false);
                        setRechargeForm({
                          type: 'email',
                          credits: 100,
                          paymentMethod: 'card',
                          useBulkPricing: false,
                          selectedBulkTier: null
                        });
                      }, 2000);
                    }}
                    onFailure={(error) => {
                      console.log('❌ Payment failed:', error);
                      triggerAlert('Payment Failed', error.message || 'Payment could not be processed.', 'error');
                    }}
                    onClose={() => {
                      setShowRechargeModal(false);
                    }}
                  >
                    <button 
                      type="button" 
                      className="w-full py-2.5 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700"
                    >
                      Pay Now
                    </button>
                  </RazorpayCheckout>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================
            BUNDLE DETAILS MODAL WITH TAX INFO
            ============================================ */}
        {selectedBundleDetails && (
          <div className="fixed inset-0 bg-slate-900/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-xl overflow-hidden p-4 space-y-3">
              <div className="flex justify-between items-center border-b pb-3">
                <h3 className="text-sm font-bold text-slate-900">{selectedBundleDetails.name}</h3>
                <button onClick={() => setSelectedBundleDetails(null)} className="p-1 bg-slate-100 rounded-full">
                  <FiX className="w-4 h-4 text-slate-500" />
                </button>
              </div>
              <div className="space-y-2 text-xs">
                <p className="text-slate-600">{selectedBundleDetails.description}</p>
                <div className="bg-slate-50 p-3 rounded-lg space-y-1">
                  <p><span className="font-bold">Price:</span> {formatPrice(selectedBundleDetails.price)}</p>
                  <p><span className="font-bold">Includes:</span> {selectedBundleDetails.emailCredits} emails, {selectedBundleDetails.smsCredits} SMS</p>
                  
                  {/* Tax Breakdown */}
                  {selectedBundleDetails.priceBreakdown && (
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      <p className="font-bold text-slate-700">Tax Breakdown:</p>
                      <div className="grid grid-cols-2 gap-1 text-[10px] mt-1">
                        <span className="text-slate-500">Tax Name:</span>
                        <span className="font-bold">{selectedBundleDetails.priceBreakdown.tax?.name || 'N/A'}</span>
                        <span className="text-slate-500">Tax Rate:</span>
                        <span className="font-bold">{selectedBundleDetails.priceBreakdown.tax?.percentage || 0}%</span>
                        <span className="text-slate-500">Tax Amount:</span>
                        <span className="font-bold text-amber-600">
                          {formatPrice(selectedBundleDetails.priceBreakdown.tax?.amount || 0)}
                        </span>
                        <span className="text-slate-500">Tax Type:</span>
                        <span className={`font-bold capitalize ${
                          selectedBundleDetails.priceBreakdown.tax?.type === 'inclusive' ? 'text-green-600' : 'text-blue-600'
                        }`}>
                          {selectedBundleDetails.priceBreakdown.tax?.type === 'inclusive' ? 'Inclusive ✅' : 'Exclusive ➕'}
                        </span>
                        <span className="text-slate-500">Source:</span>
                        <span className="font-bold capitalize">{selectedBundleDetails.priceBreakdown.tax?.source || 'Global'}</span>
                        <span className="text-slate-500">Subtotal:</span>
                        <span className="font-bold">{formatPrice(selectedBundleDetails.priceBreakdown.subtotal || 0)}</span>
                        <span className="text-slate-500 font-bold">Total:</span>
                        <span className="font-bold text-indigo-600">{formatPrice(selectedBundleDetails.priceBreakdown.total || 0)}</span>
                      </div>
                    </div>
                  )}
                </div>
                {selectedBundleDetails.features && (
                  <div>
                    <p className="font-bold text-slate-700 mb-1">Features:</p>
                    <ul className="space-y-0.5">
                      {selectedBundleDetails.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-1.5 text-slate-600">
                          <FiCheck className="text-emerald-500 w-3 h-3" /> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-2 border-t">
                <button onClick={() => setSelectedBundleDetails(null)} className="w-full py-2.5 bg-slate-100 rounded-lg font-bold text-slate-700 text-xs">
                  Close
                </button>
                <RazorpayCheckout
                  paymentType="bundle"
                  bundleId={selectedBundleDetails._id}
                  onSuccess={(data) => {
                    console.log('✅ Bundle purchase success:', data);
                    fetchAllData();
                    setSelectedBundleDetails(null);
                    triggerAlert('Success', 'Bundle purchased successfully!', 'success');
                  }}
                  onFailure={(error) => {
                    console.log('❌ Bundle purchase failed:', error);
                    triggerAlert('Purchase Failed', error.message || 'Could not purchase bundle.', 'error');
                  }}
                  onClose={() => {
                    setSelectedBundleDetails(null);
                  }}
                >
                  <button className="w-full py-2.5 bg-indigo-600 text-white rounded-lg font-bold text-xs hover:bg-indigo-700">
                    Buy Now
                  </button>
                </RazorpayCheckout>
              </div>
            </div>
          </div>
        )}

        {/* ============================================
            LOG DETAILS MODAL
            ============================================ */}
        {selectedLog && (
          <div className="fixed inset-0 bg-slate-900/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-xl overflow-hidden p-4 space-y-3">
              <div className="flex justify-between items-center border-b pb-3">
                <h3 className="text-sm font-bold text-slate-900">Message Details</h3>
                <button onClick={() => setSelectedLog(null)} className="p-1 bg-slate-100 rounded-full">
                  <FiX className="w-4 h-4 text-slate-500" />
                </button>
              </div>
              <div className="space-y-2 text-xs">
                <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded-lg">
                  <div>
                    <span className="text-slate-500">Type:</span>
                    <p className="font-bold text-slate-900 capitalize">{selectedLog.type}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Status:</span>
                    <p className={`font-bold uppercase ${selectedLog.status === 'failed' ? 'text-rose-600' : 'text-emerald-600'}`}>
                      {selectedLog.status}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Recipient:</span>
                    <p className="font-bold text-slate-900 break-all">{selectedLog.recipient}</p>
                  </div>
                  {selectedLog.sentAt && (
                    <div className="col-span-2">
                      <span className="text-slate-500">Sent:</span>
                      <p className="font-bold text-slate-900">{formatDate(selectedLog.sentAt)}</p>
                    </div>
                  )}
                  {selectedLog.creditsUsed && (
                    <div className="col-span-2">
                      <span className="text-slate-500">Credits Used:</span>
                      <p className="font-bold text-slate-900">{selectedLog.creditsUsed}</p>
                    </div>
                  )}
                </div>
                {selectedLog.message && (
                  <div>
                    <span className="text-slate-500 text-[10px] uppercase font-bold">Message:</span>
                    <p className="mt-1 p-3 bg-slate-50 rounded-lg text-slate-700 text-[11px] break-all max-h-32 overflow-y-auto">
                      {selectedLog.message}
                    </p>
                  </div>
                )}
              </div>
              <button onClick={() => setSelectedLog(null)} className="w-full py-2.5 bg-slate-100 rounded-lg font-bold text-slate-700 text-xs hover:bg-slate-200">
                Close
              </button>
            </div>
          </div>
        )}

        {/* ============================================
            ALERT MODAL
            ============================================ */}
        {alertConfig.show && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-3 text-center shadow-xl">
              <div className={`p-3 rounded-full w-fit mx-auto ${
                alertConfig.type === 'error' ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'
              }`}>
                <FiAlertCircle className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-slate-900">{alertConfig.title}</h3>
              <p className="text-xs text-slate-500 whitespace-pre-line">{alertConfig.message}</p>
              <button 
                onClick={() => setAlertConfig(prev => ({...prev, show: false}))} 
                className="w-full py-2.5 bg-slate-900 text-white font-bold text-xs rounded-xl hover:bg-slate-800"
              >
                Got it
              </button>
            </div>
          </div>
        )}

        {/* ============================================
            CONFIRMATION MODAL
            ============================================ */}
        {confirmConfig.show && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-4 shadow-xl">
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
                  <FiCreditCard className="w-5 h-5" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">{confirmConfig.title}</h3>
                  <p className="text-xs text-slate-500 whitespace-pre-line">{confirmConfig.message}</p>
                </div>
              </div>
              <div className="flex gap-2 text-xs font-bold">
                <button 
                  onClick={() => setConfirmConfig(prev => ({...prev, show: false}))} 
                  className="w-full py-2.5 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmConfig.onConfirm} 
                  className="w-full py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </TenantAdminLayout>
  );
};

export default CommunicationPanel;